import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import { useFormContext } from 'react-hook-form';
import uuid from 'uuid';

import { TextInstruction, InstructionTypes } from '../../../../types/instructions';
import { selectTextInstruction } from '../../../../store/modules/script-editor/selectors';
import { setOpenTextInstructionModal } from '../../../../store/modules/script-editor/actions';
import { parseJsonToEditorStateWithInstruction } from '../../../../services/editorJS/utils';
import { emitInstructionEventByEditor, removeInstructionElement, setInstructionActionTooltip } from '../utils';
import { InstructionActionTooltip, InstructionEvent } from '../types';
import { ScreenLocations } from '../../../../configs/instructionConfig';

import {
  ModalComponent,
  InputComponent,
  SelectComponent,
} from '../components';
import DuplicateBlock from '../../../../services/editorJS/DuplicateBlock';
import { notify } from '../../../../store/modules/notifications/actions';

type Props = {
  instanceEditor: EditorJS | undefined;
};

const scriptFieldName = 'script';
const defaultState = {
  comment: '',
  text: '',
  screenLocation: 0,
};

const TextInstructionModal = ({ instanceEditor }: Props) => {
  const dispatch = useDispatch();
  const { getValues, setValue } = useFormContext();
  const {
    isOpenModal, instructionId, text: baseText, isInline,
  } = useSelector(selectTextInstruction);
  const [comment, setComment] = useState(defaultState.comment);
  const [text, setText] = useState(defaultState.text);
  const [screenLocation, setScreenLocation] = useState(defaultState.screenLocation);
  const [initialState, setInitialState] = useState(defaultState);

  useEffect(() => {
    (async () => {
      if (instructionId) {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instruction = scriptData.instructions.find(
          (item) => item.id === instructionId && item.type === InstructionTypes.textInstruction,
        );
        if (instruction) {
          const { data } = instruction as TextInstruction;
          setComment(instruction.comment);
          setText(data.text);
          setScreenLocation(data.screenLocation);
          setInitialState({
            comment: instruction.comment,
            text: data.text,
            screenLocation: data.screenLocation,
          });
        } else {
          setComment(defaultState.comment);
          setText(baseText);
          setScreenLocation(defaultState.screenLocation);
          setInitialState(defaultState);
        }
      } else {
        setComment(defaultState.comment);
        setText(baseText);
        setScreenLocation(defaultState.screenLocation);
        setInitialState(defaultState);
      }
    })();
  }, [
    setComment,
    setText,
    setScreenLocation,
    instructionId,
    getValues,
    setInitialState,
    baseText,
  ]);

  const closeHandler = useCallback(() => {
    if (instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.SET_TEXT_INSTRUCTION_SELECTION,
        {},
      );
    }
    dispatch(setOpenTextInstructionModal(false));
  }, [dispatch, instanceEditor]);

  const getSummary = useCallback(() => {
    if (text.trim()) {
      return text;
    }
    if (comment.trim()) {
      return comment;
    }
    return 'Text';
  }, [comment, text]);

  const saveHandler = useCallback(() => {
    (async () => {
      if (instanceEditor) {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instructionIndex = scriptData.instructions.findIndex(
          (item) => item.id === instructionId && item.type === InstructionTypes.textInstruction,
        );
        const textInstruction: TextInstruction = {
          id: instructionId,
          type: InstructionTypes.textInstruction,
          comment,
          needsInfo: false,
          data: {
            screenLocation,
            text,
          },
        };
        const summary = getSummary();
        if (instructionIndex !== -1) {
          scriptData.instructions[instructionIndex] = textInstruction;
          if (isInline) {
            setInstructionActionTooltip(
              {
                instructionId,
                tooltip: `${InstructionActionTooltip.text}: ${summary}`,
                scriptBlocks: scriptData.blocks,
              },
            );
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.UPDATE_TEXT_ACTION_EVENT_LISTENERS,
              {
                instanceEditor,
                refEditor: window.scriptEditorRef!,
              },
            );
          }
          instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
        } else {
          const id = (instructionId.trim()) ? instructionId : uuid();
          textInstruction.id = id;
          scriptData.instructions.push(textInstruction);
          if (isInline) {
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.SAVE_TEXT_INSTRUCTION,
              { instructionId: id, tooltip: summary },
            );
          }
        }
        if (!isInline) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.SAVE_TEXT_INSTRUCTION_BLOCK,
            { instructionId: textInstruction.id, text: summary, tooltip: summary },
          );
        }
        setValue(scriptFieldName, JSON.stringify(scriptData));
        closeHandler();
      }
    })();
  }, [
    instanceEditor,
    getValues,
    setValue,
    instructionId,
    closeHandler,
    comment,
    text,
    screenLocation,
    isInline,
    getSummary,
  ]);

  const cancelHandler = useCallback(() => {
    (async () => {
      setComment(initialState.comment);
      setText(initialState.text);
      setScreenLocation(initialState.screenLocation);
      const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
      const isInstructionCreated = scriptData.instructions.some(
        (item) => item.id === instructionId && item.type === InstructionTypes.documentInstruction,
      ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
      if (instanceEditor) {
        if (!isInstructionCreated) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
            {},
          );
        }
        emitInstructionEventByEditor(
          instanceEditor,
          InstructionEvent.CANCEL_INSTRUCTION,
          {},
        );
      }
      closeHandler();
    })();
  }, [
    closeHandler,
    setComment,
    setText,
    setScreenLocation,
    initialState,
    instanceEditor,
    instructionId,
    getValues,
  ]);

  const removeHandler = useCallback(() => {
    if (!isInline && instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
        {},
      );
    } else {
      removeInstructionElement({ instructionId, isInline })
        .then(() => {
          if (instanceEditor) {
            instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
          }
        });
    }
    closeHandler();
  }, [closeHandler, instructionId, isInline, instanceEditor]);

  const duplicate = useCallback(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues().script);
    const notifHandler = () => dispatch(
      notify({
        type: 'success',
        icon: 'copy',
        message: 'Instruction duplicated.',
      }),
    );
    if (instanceEditor) {
      const duplicateBlockService = new DuplicateBlock(instanceEditor, notifHandler, setValue, instructionId, scriptData);
      if (!isInline) {
        duplicateBlockService.duplicateBlock();
      } else {
        duplicateBlockService.duplicateInline();
      }
    }
  }, [instanceEditor, getValues, instructionId, isInline, setValue, dispatch]);

  const showKebabMenu = useMemo(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
    return scriptData.instructions.some(
      (item) => item.id === instructionId,
    ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
  }, [getValues, instructionId]);

  return (
    <ModalComponent
      open={isOpenModal}
      title={getSummary()}
      saveHandler={saveHandler}
      cancelHandler={cancelHandler}
      deleteHandler={removeHandler}
      duplicate={duplicate}
      showKebabMenu={showKebabMenu}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Comment"
            value={comment}
            onChange={setComment}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Text"
            value={text}
            onChange={setText}
          />
        </Grid>
        <Grid item xs={6}>
          <SelectComponent
            label="Screen Location"
            value={screenLocation}
            items={ScreenLocations}
            changeHandler={setScreenLocation}
            isNone={false}
          />
        </Grid>
      </Grid>
    </ModalComponent>
  );
};

export default TextInstructionModal;
